import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import type { Content } from '@plone/types';
import { getQueryStringResults } from '@plone/volto/actions';
import { useDispatch } from 'react-redux';
import { Config } from './InfoEventConfig';
import { InfoEventMessages as messages } from './InfoEventMessages';
import { AnyAction } from 'redux';
import {
  getSubjectSearchQuery,
  getNonSubjectSearchQuery,
} from './InfoEventQuerys';

import { Button } from '@designsystem/stories/02-atoms/Button';
import { Listing } from '@designsystem/stories/04-organisms/Listing';
import { usePagination } from '@fhnw/components/Blocks/Application/hooks';
import {
  transformToCards,
  NormalizableContent,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import { getListingImageSizes } from '@fhnw/helpers/DesignSystem/imageSizes';
import { isOccasionVisible } from '@fhnw/helpers/DesignSystem/utils';
import type { OccasionProps } from '@fhnw/types/occasion';
import type { RegisterContent } from '@fhnw/helpers/DesignSystem/types';
import { useOccasionRegistration } from '@fhnw/components/OccasionRegistrationPanel';

const emptyArray: Content[] = [];

const View = ({
  content,
  data,
}: {
  content: Content;
  data: {
    info_event_subjects?: string[];
    info_event_roominfo?: string;
    info_event_emptytext?: string;
  };
}) => {
  const intl = useIntl();
  const { openForOccasion } = useOccasionRegistration();

  const onAnmelden = (occ: RegisterContent) =>
    openForOccasion({
      uid: occ.UID as string | undefined,
      event: (occ.parent ?? content) as any,
      occasion: occ as any,
    });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const { maxItems, showMore } = usePagination(Config.maxItems);

  const dispatch = useDispatch();
  const uid = useSelector(
    (state: { content: { data: Content } }) => state.content.data?.UID,
  );
  const subrequestId = `info-events-${uid}`;

  useEffect(() => {
    if (!uid) return;

    let queryString = [];
    setIsLoading(true);
    setHasError(false);

    if (data?.info_event_subjects && data.info_event_subjects?.length >= 1) {
      queryString = getSubjectSearchQuery(data.info_event_subjects, uid);
    } else {
      queryString = getNonSubjectSearchQuery(uid);
    }

    const fetchQueryResults = async () => {
      try {
        const result = await dispatch(
          getQueryStringResults(
            `/${intl.locale}`,
            {
              // Load beyond maxItems: occasions the client-side filter
              // discards must not starve the list, and "Show more" needs
              // to know about every upcoming occasion (#1708).
              limit: Config.maxLoad,
              fullobjects: true,
              sort_on: Config.sortOn,
              query: queryString,
            },
            subrequestId,
            null,
          ) as AnyAction,
        );

        if (!result?.items) {
          setHasError(true);
        }
      } catch (error) {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQueryResults();
  }, [subrequestId, intl.locale, uid, dispatch, data.info_event_subjects]);

  const infoEventTeaserList: Content[] = useSelector(
    (state: {
      querystringsearch: {
        subrequests: { [key: string]: { items: Content[] } };
      };
    }) =>
      state?.querystringsearch?.subrequests?.[subrequestId]?.items ||
      emptyArray,
  );

  const visibleInfoEvents = infoEventTeaserList.filter((occasion) =>
    isOccasionVisible(occasion as OccasionProps, occasion?.parent as Content),
  );
  const hasMore = visibleInfoEvents.length > maxItems;

  return (
    <>
      {visibleInfoEvents.length > 0 && (
        <>
          <Listing
            type="event"
            items={transformToCards(
              visibleInfoEvents.slice(0, maxItems) as NormalizableContent[],
              intl,
              'register-event',
              undefined,
              undefined,
              false,
              getListingImageSizes('event'),
              onAnmelden,
            )}
          />
          {hasMore && (
            <p>
              <Button
                label={intl.formatMessage(messages.show_more)}
                type="secondary"
                onClick={showMore}
              />
            </p>
          )}
        </>
      )}

      {/* EMPTY LIST TEXT */}
      {visibleInfoEvents.length === 0 && !isLoading && (
        <p aria-live="polite">{data.info_event_emptytext}</p>
      )}

      {/* LOADING STATE */}
      {isLoading && (
        <p aria-live="polite" aria-busy="true">
          Loading...
        </p>
      )}

      {/* ERROR STATE */}
      {hasError && (
        <p aria-live="assertive" role="alert">
          {intl.formatMessage(messages.no_results)}
        </p>
      )}

      {/* ROOM INFORMATION */}
      {data.info_event_roominfo && <p>{data.info_event_roominfo}</p>}
    </>
  );
};

export default View;
